import NoWrapTableCell from 'Components/NoWrapTableCell'
import { Checkbox } from '@mui/material'
import Box from '@mui/material/Box'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { visuallyHidden } from '@mui/utils'
import { Fragment } from 'react'

export interface HeadCell {
  id: string
  label: string
  sortable?: boolean
  numeric?: boolean
  disablePadding?: boolean
}

function SortableCell({
  headCell,
  ordering,
  orderBy,
  createSortHandler
}: Readonly<{
  headCell: HeadCell
  ordering: string
  orderBy: string
  createSortHandler: (property: string) => (event: React.MouseEvent<unknown>) => void
}>) {
  return (
    <NoWrapTableCell
      title={headCell.label}
      align={headCell.numeric ? 'right' : 'left'}
      padding={headCell.disablePadding ? 'none' : 'normal'}
      sortDirection={!orderBy.includes('-') ? false : 'desc'}
    >
      <TableSortLabel
        active={ordering === headCell.id}
        direction={!orderBy.includes('-') ? 'asc' : 'desc'}
        onClick={createSortHandler(headCell.id)}
      >
        {headCell.label}
        {ordering === headCell.id ? (
          <Box component="span" sx={visuallyHidden}>
            {!orderBy.includes('-') ? 'sorted ascending' : 'sorted descending'}
          </Box>
        ) : null}
      </TableSortLabel>
    </NoWrapTableCell>
  )
}

function NonSortableCell({ headCell }: Readonly<{ headCell: HeadCell }>) {
  return (
    <NoWrapTableCell
      title={headCell.label}
      align={headCell.numeric ? 'right' : 'left'}
      padding={headCell.disablePadding ? 'none' : 'normal'}
    >
      {headCell.label}
    </NoWrapTableCell>
  )
}

interface EnhancedTableHeadProps {
  headCells: HeadCell[]
  onRequestSort: (event: React.MouseEvent<unknown>, property: string) => void
  orderBy: string
  selectChecked?: boolean
  onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void
}

function EnhancedTableHead(props: Readonly<EnhancedTableHeadProps>) {
  const { headCells, orderBy, onRequestSort, selectChecked, onSelectAllClick } = props
  const ordering = orderBy.replace('-', '')
  const createSortHandler = (property: string) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  return (
    <TableHead>
      <TableRow>
        {onSelectAllClick && (
          <NoWrapTableCell sx={{ maxWidth: 115 }}>
            <Checkbox
              checked={selectChecked}
              size="small"
              sx={{ p: 0 }}
              onChange={onSelectAllClick}
            />
          </NoWrapTableCell>
        )}
        {headCells.map(headCell => (
          <Fragment key={headCell.id}>
            {headCell.sortable ? (
              <SortableCell
                headCell={headCell}
                ordering={ordering}
                orderBy={orderBy}
                createSortHandler={createSortHandler}
              />
            ) : (
              <NonSortableCell headCell={headCell} />
            )}
          </Fragment>
        ))}
      </TableRow>
    </TableHead>
  )
}

export default EnhancedTableHead
